import { BUILD_ENVIRONMENT, BUILD_PLATFORM } from "../../../base/common/$__metadata.js";
import { toDisposable, type IDisposable } from "../../../base/common/lifecycle.js";
import type { IAnalyticsService } from "../../analytics/common/analytics.js";
import type { ILogService } from "../../log/common/log.js";
import type { INotificationService } from "../../notifications/common/notifications.js";
import type { ILicenseService, InspectorAccessReason } from "./licenses.js";

export function isFreeWeekendOverride(environment: string, search: string): boolean {
    return environment === "dev" && new URLSearchParams(search).has("free-weekend");
}

export function isFreeWeekendForced(): boolean {
    return isFreeWeekendOverride(
        BUILD_ENVIRONMENT,
        typeof location === "undefined" ? "" : location.search,
    );
}

export function shouldMonitorInspectorAccess(): boolean {
    return BUILD_PLATFORM === "chrome-extension" || BUILD_PLATFORM === "embed" || isFreeWeekendForced();
}

export function canUseAccountGatedFeature(isLoggedIn: boolean, inspectorAccessAllowed: boolean): boolean {
    return isLoggedIn || inspectorAccessAllowed;
}

const announced = new WeakSet<object>();

type FreeWeekendAccess = Pick<ILicenseService, "inspectorAccessReason" | "refreshInspectorAccess">;

export function reportInspectorAccessFailure(
    error: unknown,
    logger: Pick<ILogService, "warn">,
    analytics: Pick<IAnalyticsService, "logEvent"> | null,
): void {
    logger.warn("Inspector weekend access unavailable", error);
    analytics?.logEvent("inspector.free_weekend.time_unavailable", {
        error: error instanceof Error ? error.message.substring(0, 300) : String(error).substring(0, 300),
    });
}

/** Refresh promotional access and announce it once for this editor session. */
export async function announceFreeWeekend(
    access: FreeWeekendAccess,
    notifications: Pick<INotificationService, "showNotification">,
): Promise<void> {
    if (access.inspectorAccessReason !== "free-weekend") {
        await access.refreshInspectorAccess();
    }
    if (access.inspectorAccessReason !== "free-weekend" || announced.has(access as object)) return;
    announced.add(access as object);
    notifications.showNotification({
        type: "success",
        message: "Free Inspector Weekend — all Inspector features are unlocked through Sunday. Try editing, full resource inspection, node graphs, AI tools, and exports.",
        duration: 20_000,
    });
}

/** Keep access current for long-running editor sessions using elapsed intervals only. */
export function monitorInspectorAccess(
    access: FreeWeekendAccess,
    notifications: Pick<INotificationService, "showNotification">,
    onError: (error: unknown) => void,
    intervalMilliseconds = 15 * 60 * 1_000,
): IDisposable {
    const refresh = () => {
        void announceFreeWeekend(access, notifications).catch(onError);
    };
    refresh();
    const interval = setInterval(refresh, intervalMilliseconds);
    return toDisposable(() => clearInterval(interval));
}

export function inspectorAccessReason(
    licensed: boolean | null,
    serverWeekend: boolean | null,
    forcedWeekend = isFreeWeekendForced(),
    serverWeekendAllowed = BUILD_PLATFORM === "chrome-extension",
): InspectorAccessReason {
    if (forcedWeekend) return "free-weekend";
    if (licensed === true) return "license";
    if (serverWeekendAllowed && serverWeekend === true) return "free-weekend";
    return null;
}
